import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiGlobe, FiServer, FiLoader, FiCheckCircle, FiChevronDown, FiSearch } from 'react-icons/fi';
import { panelsApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import CURRENCIES from '../../utils/currencies';
import toast from 'react-hot-toast';

export default function CreatePanelModal({ onClose, onCreate }) {
  const { user } = useAuth();
  const [domain, setDomain] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [providerUrl, setProviderUrl] = useState(user?.provider_url || '');
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [nameServers, setNameServers] = useState([]);
  const [cloudflareMode, setCloudflareMode] = useState('auto');
  const [verifyResult, setVerifyResult] = useState(null);
  const [currSearch, setCurrSearch] = useState('');
  const [currOpen, setCurrOpen] = useState(false);
  const currRef = useRef(null);

  const filteredCurrencies = CURRENCIES.filter(c =>
    c.code.toLowerCase().includes(currSearch.toLowerCase()) ||
    c.name.toLowerCase().includes(currSearch.toLowerCase())
  );

  useEffect(() => {
    function handleClick(e) {
      if (currRef.current && !currRef.current.contains(e.target)) setCurrOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const skipCloudflare = cloudflareMode === 'skip';
  const canSubmit = !!domain.trim() && (skipCloudflare || isVerified);

  const resetVerification = () => {
    setIsVerified(false);
    setNameServers([]);
    setVerifyResult(null);
  };

  const handleModeChange = (mode) => {
    setCloudflareMode(mode);
    resetVerification();
  };

  const handleVerify = async () => {
    if (!domain.trim()) {
      toast.error('Please enter a domain first');
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await panelsApi.verifyDomain(domain.trim().toLowerCase(), cloudflareMode);
      setNameServers(res.data.nameServers || []);
      setVerifyResult(res.data);
      setIsVerified(true);
      toast.success(res.data.usingExistingZone
        ? `Found existing zone: ${res.data.zoneName}`
        : 'Domain verified in Cloudflare');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to verify domain in Cloudflare');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDomainChange = (e) => {
    setDomain(e.target.value);
    resetVerification();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);

    let formattedUrl = providerUrl.trim();
    if (formattedUrl && !formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    try {
      await onCreate({
        domain: domain.trim().toLowerCase(),
        phpVersion: '83',
        currency,
        adminUsername: adminUsername.trim(),
        adminPassword: adminPassword.trim(),
        providerUrl: formattedUrl,
        cloudflareMode
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 w-full max-w-lg shadow-2xl relative my-8">
        
        <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3">
            <div className="p-2 bg-primary-50 text-primary-600 rounded-lg">
              <FiServer size={20} />
            </div>
            Create New Child Panel
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Cloudflare handling */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Cloudflare DNS
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'auto', label: 'Auto', hint: 'Create zone if needed' },
                { id: 'existing', label: 'Existing zone', hint: 'Domain already on Cloudflare' },
                { id: 'skip', label: 'Skip', hint: 'I add DNS myself' }
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleModeChange(opt.id)}
                  disabled={loading || verifyLoading}
                  className={`rounded-xl border px-3 py-2.5 text-left transition-all disabled:opacity-60 ${cloudflareMode === opt.id ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}
                >
                  <span className={`block text-sm font-semibold ${cloudflareMode === opt.id ? 'text-primary-700' : 'text-slate-700'}`}>{opt.label}</span>
                  <span className="block text-[11px] leading-tight text-slate-500 mt-0.5">{opt.hint}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Domain */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              <FiGlobe size={14} /> Domain
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g. shop.example.com"
                value={domain}
                onChange={handleDomainChange}
                disabled={isVerified || loading || verifyLoading}
                className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all placeholder-slate-400 disabled:opacity-60"
                required
                autoFocus
              />
              {!skipCloudflare && (!isVerified ? (
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={verifyLoading || !domain.trim()}
                  className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                >
                  {verifyLoading ? <FiLoader className="animate-spin" /> : 'Verify'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={resetVerification}
                  disabled={loading}
                  className="px-4 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-medium transition-colors whitespace-nowrap"
                >
                  Edit
                </button>
              ))}
            </div>

            {skipCloudflare && (
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <p className="text-sm text-slate-700 font-medium flex items-center gap-2">
                  <FiCheckCircle /> Cloudflare is skipped. After creation, add an A record for this domain pointing to your server IP yourself.
                </p>
              </div>
            )}

            {!skipCloudflare && !isVerified && (
              <p className="mt-2 text-xs text-slate-500">
                {cloudflareMode === 'existing'
                  ? 'Enter the subdomain (e.g. shop.example.com) and click Verify — it must sit under a domain already on Cloudflare.'
                  : 'Enter the full domain and click Verify to configure Cloudflare.'}
              </p>
            )}

            {!skipCloudflare && isVerified && verifyResult?.usingExistingZone && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                  <FiCheckCircle /> Using existing Cloudflare zone: <span className="font-mono">{verifyResult.zoneName}</span>
                </p>
                <p className="mt-1 text-xs text-green-700">The A record for {domain.trim().toLowerCase()} will be added automatically — no nameserver change needed.</p>
              </div>
            )}

            {!skipCloudflare && isVerified && !verifyResult?.usingExistingZone && nameServers.length > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800 font-medium flex items-center gap-2 mb-2">
                  <FiCheckCircle /> Please point your domain to these Nameservers:
                </p>
                <ul className="text-sm text-green-700 list-disc list-inside">
                  {nameServers.map((ns, idx) => (
                    <li key={idx} className="font-mono">{ns}</li>
                  ))}
                </ul>
              </div>
            )}

            {!skipCloudflare && isVerified && !verifyResult?.usingExistingZone && nameServers.length === 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                  <FiCheckCircle /> Domain verified. Cloudflare configuration is active.
                </p>
              </div>
            )}
          </div>

          {/* Provider URL */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              <FiGlobe size={14} /> Provider URL (Configured in Settings)
            </label>
            <input
              type="text"
              placeholder="Configure in settings"
              value={providerUrl}
              onChange={(e) => setProviderUrl(e.target.value)}
              disabled={true}
              className="w-full bg-slate-100 border border-slate-200 text-slate-500 rounded-xl px-4 py-3 focus:outline-none transition-all placeholder-slate-400 cursor-not-allowed"
              required
            />
          </div>

          {/* Currency */}
          <div ref={currRef} className="relative">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Currency
            </label>
            <button type="button" onClick={() => setCurrOpen(!currOpen)}
              className="w-full flex items-center justify-between bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all">
              <span>{currency}</span>
              <FiChevronDown size={16} className="text-slate-400" />
            </button>
            {currOpen && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                  <FiSearch size={14} className="text-slate-400" />
                  <input type="text" value={currSearch} onChange={e => setCurrSearch(e.target.value)}
                    placeholder="Search currency..." autoFocus
                    className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder-slate-400" />
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredCurrencies.map(c => (
                    <button key={c.code} type="button"
                      onClick={() => { setCurrency(c.code); setCurrOpen(false); setCurrSearch(''); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 ${currency === c.code ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-700'}`}>
                      <span className="font-mono w-10">{c.code}</span>
                      <span className="text-slate-500 text-xs">{c.name}</span>
                    </button>
                  ))}
                  {filteredCurrencies.length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">No currencies found</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Admin Username */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                Admin Username
              </label>
              <input
                type="text"
                placeholder="Leave blank to auto-generate"
                value={adminUsername}
                onChange={(e) => setAdminUsername(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all placeholder-slate-400"
              />
            </div>

            {/* Admin Password */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                Admin Password
              </label>
              <input
                type="text"
                placeholder="Leave blank to auto-generate"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all placeholder-slate-400"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button 
              type="button" 
              onClick={onClose} 
              disabled={loading}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !canSubmit}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white font-semibold shadow-lg shadow-primary-500/30 transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><FiLoader className="animate-spin" /> Provisioning...</>
              ) : (
                'Create Panel'
              )}
            </button>
          </div>
          
        </form>
      </div>
    </div>
  );
}
